import { z } from 'zod';
import { emailSchema } from './common';
import { profileWithRolesShape } from './profile';

export const signInRequest = z.object({
  email: emailSchema,
  password: z.string().min(1).max(128),
});
export type SignInRequest = z.infer<typeof signInRequest>;

/**
 * Successful sign-in / sign-up response. Includes the bearer token so the client
 * can store it (httpOnly cookie set in Set-Cookie too — token is also returned
 * for non-cookie clients like mobile + curl).
 */
export const authSuccessResponse = z.object({
  token: z.string(),
  expiresAt: z.number().int(),
  profile: profileWithRolesShape,
});
export type AuthSuccessResponse = z.infer<typeof authSuccessResponse>;

/** GET /me when the caller is authenticated. */
export const meResponse = z.object({
  profile: profileWithRolesShape,
});
export type MeResponse = z.infer<typeof meResponse>;

/**
 * POST /auth/change-password. Requires the current password to be correct —
 * this is a privilege-confirming rotation, not a recovery flow.
 *
 *   - Old password is checked with the same constant-time-ish path as sign-in.
 *   - On success: existing sessions stay alive (no "sign out everywhere" yet).
 */
export const changePasswordRequest = z
  .object({
    currentPassword: z.string().min(1).max(128),
    newPassword: z.string().min(8).max(128),
  })
  .refine((d) => d.currentPassword !== d.newPassword, {
    message: 'New password must differ from the current password',
    path: ['newPassword'],
  });
export type ChangePasswordRequest = z.infer<typeof changePasswordRequest>;
